import {StyledTooltip} from '@wandb/weave/components/DraggablePopups';
import {LoadingDots} from '@wandb/weave/components/LoadingDots';
import {loadImage} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/loadImage';
import {NotApplicable} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/NotApplicable';
import {useFileContent} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/tsDataModelHooks';
import {type ContentViewMetadataLoadedProps} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/typeViews/Content/types';
import {SingleImageLightbox} from '@wandb/weave/components/PagePanelComponents/LightboxImage';
import React, {useEffect, useMemo, useState} from 'react';
import {AutoSizer} from 'react-virtualized';

export const ImageContent = (props: ContentViewMetadataLoadedProps) => (
  <AutoSizer style={{height: '100%', width: '100%'}}>
    {({width, height}) => {
      if (width === 0 || height === 0) {
        return null;
      }
      return (
        <ImageContentWithSize
          {...props}
          containerWidth={width}
          containerHeight={height}
        />
      );
    }}
  </AutoSizer>
);

type ImageContentWithSizeProps = ContentViewMetadataLoadedProps & {
  containerWidth: number;
  containerHeight: number;
};

const ImageContentWithSize = ({
  entity,
  project,
  content,
  metadata,
  containerWidth,
  containerHeight,
}: ImageContentWithSizeProps) => {
  const imageBinary = useFileContent({
    entity,
    project,
    digest: content,
    skip: !content,
  });
  if (!content) {
    return <NotApplicable />;
  } else if (imageBinary.loading) {
    return <LoadingDots />;
  } else if (imageBinary.result == null) {
    return <span></span>;
  }

  return (
    <ImageContentWithData
      mimetype={metadata.mimetype}
      buffer={imageBinary.result}
      containerWidth={containerWidth}
      containerHeight={containerHeight}
    />
  );
};

type ImageContentWithDataProps = {
  mimetype: string;
  buffer: ArrayBuffer;
  containerWidth: number;
  containerHeight: number;
};

export const ImageContentWithData = ({
  mimetype,
  buffer,
  containerWidth,
  containerHeight,
}: ImageContentWithDataProps) => {
  const url = useMemo(() => {
    const blob = new Blob([buffer], {
      type: mimetype,
    });
    return URL.createObjectURL(blob);
  }, [buffer, mimetype]);

  const [imageDim, setImageDim] = useState({
    width: -1,
    height: -1,
  });
  useEffect(() => {
    setImageDim({width: -1, height: -1});
    loadImage(url, setImageDim);
  }, [url]);

  if (imageDim.width === -1 || imageDim.height === -1) {
    return <LoadingDots />;
  }
  return (
    <ImageContentLoaded
      url={url}
      imageWidth={imageDim.width}
      imageHeight={imageDim.height}
      containerWidth={containerWidth}
      containerHeight={containerHeight}
    />
  );
};

type ImageContentLoadedProps = {
  url: string;
  imageWidth: number;
  imageHeight: number;
  containerWidth: number;
  containerHeight: number;
};

const previewWidth = 300;
const previewHeight = 300;

const previewTriggerRatio = 0.8;

// Only pop the hover preview when the cell is meaningfully smaller than the
// preview, so a near-preview-size cell (e.g. the detail pane's ~284px media
// box) doesn't show a same-size, redundant popup. Requires the cell to be
// < 80% of the preview, i.e. the preview magnifies the image by at least ~25%.
const shouldShowPreview = (containerWidth: number, containerHeight: number) =>
  containerWidth < previewWidth * previewTriggerRatio ||
  containerHeight < previewHeight * previewTriggerRatio;

const ImageContentLoaded = ({
  url,
  imageWidth,
  imageHeight,
  containerWidth,
  containerHeight,
}: ImageContentLoadedProps) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const onClick = () => setLightboxOpen(true);

  let image = (
    <img
      style={{
        maxWidth: '100%',
        maxHeight: '100%',
        cursor: 'pointer',
      }}
      src={url}
      alt="Custom"
      onClick={onClick}
    />
  );

  const hasPreview = shouldShowPreview(containerWidth, containerHeight);

  if (hasPreview) {
    const preview = (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
        }}>
        <div
          style={{
            maxWidth: previewWidth,
            maxHeight: previewHeight,
            margin: 'auto',
            overflow: 'hidden',
          }}>
          <img
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
            src={url}
            alt="Custom"
            onClick={onClick}
          />
        </div>
        <div
          style={{
            color: 'var(--wb-text-tertiary)',
            textAlign: 'center',
            fontSize: '0.8em',
          }}>
          {imageWidth}x{imageHeight} - Click for more details
        </div>
      </div>
    );
    image = (
      <StyledTooltip enterDelay={500} title={preview}>
        {image}
      </StyledTooltip>
    );
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}>
        {image}
      </div>
      <SingleImageLightbox
        src={url}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        withDownload
      />
    </>
  );
};
